/**
 * CustomerSearch — typeahead search dropdown for overriding the matched
 * customer on an email. Calls GET /customers?search=... with debounce.
 *
 * If no results: shows "Add new customer" option.
 * On select: calls PATCH /inbox/{emailId}/customer with the customer_no.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { portalService } from '../../services/portal';
import type { CustomerSearchResult, LinkedOrderState } from '../../types/portal';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import AddCustomerModal from './AddCustomerModal';
import ReassignOrderConfirm from './ReassignOrderConfirm';
import { needsOrderDecision } from './customerOverrideUtils';
import styles from './CustomerSearch.module.css';

interface CustomerSearchProps {
  emailId: string;
  /** Sender display name — used to pre-fill the Add Customer modal */
  senderName?: string;
  /** Sender email address — used to pre-fill the Add Customer modal */
  senderEmail?: string;
  onOverride: () => void;
  onCancel: () => void;
  /** Surface success/error toasts from the linked-order safety-valve. */
  onToast?: (message: string, kind: 'success' | 'error') => void;
}

/** Linked orders the operator still needs to decide on (the override is done). */
interface PendingReassign {
  customerNo: string;
  customerName: string | null;
  linkedOrders: LinkedOrderState[];
}


/** Plain function — no component state needed, so no hook required. */
const searchCustomers = (q: string) => portalService.searchCustomers(q, 10);

const CustomerSearch: React.FC<CustomerSearchProps> = ({
  emailId,
  senderName,
  senderEmail,
  onOverride,
  onCancel,
  onToast,
}) => {
  const [query, setQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [showAddModal, setShowAddModal] = useState(false);
  // After the (email-only) override, linked orders that still need a decision.
  const [pending, setPending] = useState<PendingReassign | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Shared hooks
  useClickOutside(wrapperRef, onCancel, !showAddModal);
  const { results, loading, searched } = useDebouncedSearch(
    query,
    searchCustomers,
  );

  // Reset highlight when results change
  useEffect(() => {
    setHighlightIndex(-1);
  }, [results]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Apply the (email-only) override; if the email's linked order(s) diverge,
  // surface the reassignment → order safety-valve instead of finishing.
  const applyOverride = useCallback(
    async (customerNo: string, failMsg: string) => {
      setSubmitting(true);
      setError(null);
      try {
        const resp = await portalService.overrideCustomer(emailId, customerNo);
        portalService.invalidateStatsCache();
        if (needsOrderDecision(resp.linked_orders)) {
          setPending({
            customerNo: resp.customer_no,
            customerName: resp.customer_name,
            linkedOrders: resp.linked_orders,
          });
        } else {
          onOverride();
        }
      } catch {
        setError(failMsg);
      } finally {
        setSubmitting(false);
      }
    },
    [emailId, onOverride],
  );

  const handleSelect = useCallback(
    (customer: CustomerSearchResult) =>
      applyOverride(customer.sage_customer_no, 'Failed to set customer'),
    [applyOverride],
  );

  /** Called when a new customer is created via the modal. */
  const handleCustomerCreated = useCallback(
    (customerNo: string) => {
      setShowAddModal(false);
      return applyOverride(
        customerNo,
        'Customer created but failed to apply override',
      );
    },
    [applyOverride],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      // Spent here so the host listening on window / document does not also
      // close on this press (kanban 2c097936).
      e.preventDefault();
      onCancel();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0 && results[highlightIndex]) {
      e.preventDefault();
      handleSelect(results[highlightIndex]);
    }
  };

  const showDropdown = query.trim().length >= 2 && (loading || searched);

  // The email override already applied (safe, email-only). Now ask whether to
  // also correct the linked order(s) — the reassignment → order safety-valve.
  if (pending) {
    return (
      <ReassignOrderConfirm
        emailId={emailId}
        customerNo={pending.customerNo}
        customerName={pending.customerName}
        linkedOrders={pending.linkedOrders}
        onResolved={() => {
          setPending(null);
          onOverride();
        }}
        onToast={onToast}
      />
    );
  }

  return (
    <>
      <div className={styles.wrapper} ref={wrapperRef}>
        <div className={styles.inputRow}>
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            placeholder="Search customers..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={submitting}
            role="combobox"
            aria-expanded={showDropdown}
            aria-haspopup="listbox"
          />
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onCancel}
            title="Cancel"
          >
            &times;
          </button>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {showDropdown && (
          <div className={styles.dropdown} role="listbox">
            {loading && (
              <div className={styles.loading}>Searching...</div>
            )}
            {!loading && searched && results.length === 0 && (
              <div className={styles.emptyState}>
                No customers found
              </div>
            )}
            {!loading && results.map((c, i) => (
              <button
                key={c.id}
                type="button"
                className={`${styles.option} ${i === highlightIndex ? styles.optionHighlighted : ''}`}
                onClick={() => handleSelect(c)}
                disabled={submitting}
                role="option"
                aria-selected={i === highlightIndex}
                data-action="customer_pick"
                data-target-customer-no={c.sage_customer_no}
              >
                <div className={styles.optionMain}>
                  <span className={styles.optionName}>
                    {c.customer_name || 'Unknown'}
                  </span>
                  <span className={styles.optionNo}>
                    {c.sage_customer_no}
                  </span>
                </div>
                <div className={styles.optionMeta}>
                  {c.city && c.state && (
                    <span>{c.city}, {c.state}</span>
                  )}
                  {c.salesperson_name && (
                    <span className={styles.spBadge}>
                      {c.salesperson_name}
                    </span>
                  )}
                </div>
              </button>
            ))}
            {/* Add new customer button — shown after search completes */}
            {!loading && searched && (
              <button
                type="button"
                className={styles.addNewBtn}
                data-action="customer_add_new_open"
                onClick={() => setShowAddModal(true)}
                disabled={submitting}
              >
                + Add new customer
              </button>
            )}
          </div>
        )}
      </div>

      {showAddModal && (
        <AddCustomerModal
          initialName={query.trim() || senderName}
          initialEmail={senderEmail}
          onCreated={handleCustomerCreated}
          onCancel={() => setShowAddModal(false)}
        />
      )}
    </>
  );
};

export default CustomerSearch;
